import {
  useState, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Button } from '@skyline-platform/alchemy';
import './StatusToggle.css';

export const StatusToggle = ({
  online,
  entityId,
  anchorPrefix,
  onConfirm,
  triggerIcon,
  triggerAriaLabel,
  showConfirmation = true,
}) => {
  const [localOnline, setLocalOnline] = useState(online);

  // Sync local state when the parent provides updated data (e.g. after a WebSocket event)
  useEffect(() => {
    setLocalOnline(online); // eslint-disable-line react-hooks/set-state-in-effect
  }, [online]);

  const popoverRef = useRef(null);
  const backdropRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [confirmState, setConfirmState] = useState(null); // null | 'loading' | 'success' | 'error'

  const anchorName = `${anchorPrefix}${entityId}`;

  const close = () => {
    popoverRef.current?.hidePopover();
    backdropRef.current?.hidePopover();
    setIsOpen(false);
    setTimeout(() => setConfirmState(null), 1000); // allows css transition to complete
  };

  const handleConfirm = async () => {
    setConfirmState('loading');
    try {
      const result = await onConfirm(localOnline);
      if (result?.error) {
        if (showConfirmation) setConfirmState('error');
      } else {
        if (showConfirmation) setConfirmState('success');
        setLocalOnline(!localOnline);
      }
    } catch {
      if (showConfirmation) setConfirmState('error');
    } finally {
      if (showConfirmation) {
        setTimeout(close, 3000);
      } else {
        setConfirmState(null);
      }
    }
  };

  const handleTriggerClick = () => {
    if (!showConfirmation) {
      handleConfirm();
      return;
    }
    if (isOpen) {
      close();
    } else {
      // Backdrop pushed first so it sits below the popover in the top-layer stack.
      // This blocks hover/pointer events on all underlying page content while open.
      backdropRef.current?.showPopover();
      popoverRef.current?.showPopover();
      setIsOpen(true);
    }
  };

  // Close on Escape key while popover is open.
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const actionLabel = localOnline ? 'Set offline' : 'Set online';

  return (
    <status-toggle>
      <button
        style={{ anchorName }}
        onClick={handleTriggerClick}
        aria-haspopup={showConfirmation ? 'true' : undefined}
        aria-label={triggerAriaLabel}
        disabled={!showConfirmation && confirmState === 'loading'}
        tabIndex={0}
      >
        {triggerIcon}
      </button>

      {showConfirmation && (
        <>
          {/* Full-viewport transparent backdrop — sits below popover in top-layer, blocks
              pointer events to all underlying content while the popover is open. */}
          <div
            ref={backdropRef}
            popover='manual'
            className='backdrop'
            onClick={close}
          />

          <div
            ref={popoverRef}
            popover='manual'
            className='popover'
            inert={!isOpen}
            style={{ positionAnchor: anchorName }}
            onClick={(e) => {
              // Clicking the icon clone or popover background (not the button) closes it
              if (!e.target.closest('alc-button, button')) close();
            }}
          >
            {/* Visual clone of trigger icon — sits in the top-layer over the trigger */}
            <span className='icon' aria-hidden='true'>
              {triggerIcon}
            </span>
            <span
              className={`actions${confirmState ? ` is-${confirmState}` : ''}`}
              {...(confirmState === 'loading'
                ? { role: 'status', 'aria-label': 'Processing...' }
                : {})}
            >
              <Button
                label={
                  confirmState === 'success' ? 'Success!'
                    : confirmState === 'error' ? 'Error'
                      : confirmState === 'loading' ? 'Processing...'
                        : actionLabel
                }
                type='filled'
                state='default'
                handleClick={!confirmState ? handleConfirm : undefined}
              />
            </span>
          </div>
        </>
      )}
    </status-toggle>
  );
};

StatusToggle.propTypes = {
  online: PropTypes.bool.isRequired,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  anchorPrefix: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  triggerIcon: PropTypes.node.isRequired,
  triggerAriaLabel: PropTypes.string.isRequired,
  showConfirmation: PropTypes.bool,
};
